import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Eye, EyeSlash, Sliders, DotsSixVertical } from "@phosphor-icons/react";

/** Réglages → Blocs de l'accueil. Auto-save on toggle + reorder. */
export default function DashboardPrefsDialog({ open, onOpenChange, onSaved }) {
  const [prefs, setPrefs] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/users/me/dashboard-prefs")
      .then((r) => setPrefs(r.data))
      .catch((err) => toast.error(formatApiError(err)));
  }, [open]);

  // Auto-save any change (toggle or reorder). Optimistic — no explicit "save" button.
  const persist = async (next) => {
    setPrefs(next);
    try {
      await api.patch("/users/me/dashboard-prefs", { hidden: next.hidden, order: next.order });
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggle = (key) => {
    const hidden = new Set(prefs.hidden || []);
    if (hidden.has(key)) hidden.delete(key); else hidden.add(key);
    persist({ ...prefs, hidden: Array.from(hidden) });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = prefs.order.indexOf(active.id);
    const newIdx = prefs.order.indexOf(over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    persist({ ...prefs, order: arrayMove(prefs.order, oldIdx, newIdx) });
  };

  const blocksByKey = Object.fromEntries((prefs?.available_blocks || []).map((b) => [b.key, b]));
  const orderedBlocks = (prefs?.order || []).map((k) => blocksByKey[k]).filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl" data-testid="dashboard-prefs-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sliders size={18} weight="duotone" className="text-[#7CB2FF]" />
            Accueil — blocs visibles & ordre
          </DialogTitle>
          <DialogDescription className="text-white/60 text-xs">
            Tape sur l&apos;œil pour afficher/masquer. Glisse la poignée <DotsSixVertical size={12} className="inline" /> pour réorganiser. Enregistré automatiquement.
          </DialogDescription>
        </DialogHeader>

        {!prefs ? (
          <div className="h-40 rounded-xl bg-white/5 animate-pulse" />
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={prefs.order} strategy={verticalListSortingStrategy}>
              <div className="space-y-1.5">
                {orderedBlocks.map((b) => {
                  const shown = !(prefs.hidden || []).includes(b.key);
                  return <SortableRow key={b.key} block={b} shown={shown} onToggle={() => toggle(b.key)} />;
                })}
              </div>
            </SortableContext>
          </DndContext>
        )}

        <div className="text-[10px] text-white/40 text-center pt-1">
          Se synchronise entre tous tes appareils (web + iPhone).
        </div>
      </DialogContent>
    </Dialog>
  );
}


function SortableRow({ block, shown, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.key });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 30 : 1,
    opacity: isDragging ? 0.85 : 1,
    touchAction: "none",  // Critical for mobile: prevents the browser from stealing the touch as scroll
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 rounded-xl border px-2 py-2.5 ${shown ? "bg-emerald-500/10 border-emerald-500/30" : "bg-white/[0.02] border-white/10"} ${isDragging ? "shadow-[0_10px_40px_rgba(0,0,0,0.6)] border-[#7CB2FF]" : ""}`}
      data-testid={`dash-block-${block.key}`}
    >
      {/* Drag handle — plain div (not <button>) so dnd-kit sensors receive the pointer/touch events cleanly */}
      <div
        {...attributes}
        {...listeners}
        role="button"
        tabIndex={0}
        aria-label="Réordonner"
        className="w-9 h-9 rounded-lg flex items-center justify-center text-white/40 hover:text-white/70 active:text-white cursor-grab active:cursor-grabbing select-none"
        style={{ touchAction: "none" }}
        data-testid={`dash-block-handle-${block.key}`}
      >
        <DotsSixVertical size={18} weight="bold" />
      </div>
      <button
        type="button"
        onClick={onToggle}
        className="flex-1 flex items-center gap-3 text-left py-1"
        data-testid={`dash-block-toggle-${block.key}`}
      >
        {shown ? <Eye size={16} weight="duotone" className="text-emerald-400" /> : <EyeSlash size={16} weight="duotone" className="text-white/40" />}
        <div className="flex-1">
          <div className={`text-sm ${shown ? "text-white" : "text-white/50"}`}>{block.label}</div>
          <div className="text-[10px] text-white/40">{shown ? "Affiché · Tape pour cacher" : "Caché · Tape pour afficher"}</div>
        </div>
      </button>
    </div>
  );
}
